import clsx from "clsx";
import { useRouter } from "next/router";
import { ReactElement, useCallback, useEffect, useMemo, useState } from "react";
import { AlertCircle, ChevronDown, Edit3, Copy, Search } from "react-feather";
import { Button } from "src/components/rough/Button";
import { IconButton } from "src/components/rough/IconButton";
import { Page } from "src/layouts/Page";
import { Select } from "src/components/rough/Select";
import { TextInput } from "src/components/rough/TextInput";
import { toast } from "react-toastify";
import { CreateInterventionDialog } from "../../components/rough/Builders/InterventionBuilder/AddInterventionDialog";
import { InterventionCard } from "../../components/rough/Intervention";
import { InterventionCardSkeleton } from "../../components/rough/Intervention/InterventionCardSkeleton";
import { InterventionPreview } from "../../components/rough/Intervention/InterventionPreview";
import { useCustomInterventions } from "../../data/queries/useCustomInterventions";
import { CustomInterventionItem } from "../../types";
import { NextPageWithLayout } from "../_app";
import { useDuplicateIntervention } from "../../data/mutations/useCreateIntervention";
import { DuplicateDialog } from "../../components/rough/Builders/Shared/DuplicateDialog";

enum Tabs {
  All = "all",
  Published = "published",
  Draft = "draft",
  Archived = "archived",
}

const InterventionsPage: NextPageWithLayout = () => {
  const router = useRouter();
  const tab = router.query.tab;

  const { data, loading, error, refetch } = useCustomInterventions(tab === "archived");
  const [query, setQuery] = useState("");
  const interventionItems = data?.customInterventions.filter(
    (intervention) =>
      intervention.name?.toLowerCase().includes(query.toLowerCase()) ||
      intervention.description?.toLowerCase().includes(query.toLowerCase()) ||
      [
        intervention.archived ? "archived" : "active",
        intervention.draft ? "draft" : "published",
      ].some((t) => t.includes(query.toLowerCase())),
  );
  const [selectedIntervention, setSelectedIntervention] = useState<CustomInterventionItem | null>(
    null,
  );
  const [createDialogOpen, setCreateDialogOpen] = useState(false);
  const [duplicateIntervention] = useDuplicateIntervention();

  const [duplicateInterventionId, setDuplicateInterventionId] = useState<string | null>(null);
  const duplicateInterventionName = useMemo(() => {
    if (!duplicateInterventionId) return undefined;

    const intervention = interventionItems?.find((i) => i.id === duplicateInterventionId);
    return intervention?.name;
  }, [duplicateInterventionId, interventionItems]);

  const isInterventionCardSelected = (intervention: CustomInterventionItem) =>
    intervention.id === selectedIntervention?.id;

  const onInterventionCardClick = (intervention: CustomInterventionItem) => {
    if (isInterventionCardSelected(intervention)) {
      setSelectedIntervention(null);
    } else {
      setSelectedIntervention(intervention);
    }
  };

  const editIntervention = (id: string) => {
    router.push("/interventions/builder/[id]", `/interventions/builder/${id}`);
  };

  const handleDuplicateIntervention = useCallback(
    async (name: string) => {
      if (!duplicateInterventionId) return;

      try {
        const res = await duplicateIntervention({
          variables: { cloneInterventionId: duplicateInterventionId, newName: name },
        });

        const id = res.data?.cloneIntervention.id;
        if (id == null) {
          const errorMessage =
            res.errors?.map((err) => err.message).join(", ") ?? "Something went wrong";

          throw new Error(errorMessage);
        }

        router.push(`/interventions/builder/${id}`);
      } catch (err: any) {
        toast.error(err.message);
      }
    },
    [duplicateInterventionId, duplicateIntervention, router],
  );

  useEffect(() => {
    refetch();

    setSelectedIntervention(null);
    if (!router.isReady) return;

    if (!tab) {
      router.push(`/interventions?tab=${Tabs.All}`, undefined, { shallow: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [tab, router]);

  const capitalise = (str: string) => {
    return str.charAt(0).toUpperCase() + str.slice(1);
  };

  return (
    <div className="pb-24">
      <CreateInterventionDialog
        open={createDialogOpen}
        onOpenChange={() => setCreateDialogOpen(false)}
      />
      {
        // we have to use this so that the dialog is remounted when the id changes
        // otherwise the implementation of the dialog will not reset the name
        duplicateInterventionId && (
          <DuplicateDialog
            resourceName="intervention"
            open
            onOpenChange={() => setDuplicateInterventionId(null)}
            defaultName={duplicateInterventionName + " (Copy)"}
            onConfirm={handleDuplicateIntervention}
          />
        )
      }

      <div className={"relative mx-auto flex h-full w-full max-w-[80rem] flex-col px-24"}>
        <div className="mb-4 mt-12 flex w-full items-center justify-between">
          <h1 className="text-3xl font-bold">Activities</h1>
        </div>
        <div className="my-2 flex items-center justify-between px-1 py-2">
          <div className="flex items-center gap-2">
            <Select.Select
              value={String(tab)}
              onValueChange={(type) => {
                router.push(`/interventions?tab=${type}`, undefined, { shallow: true });
              }}>
              <Select.Trigger ariaLabel="timeDirectionSelector">
                <Select.Value className="capitalize">{capitalise(String(tab))}</Select.Value>
                <Select.Icon>
                  <ChevronDown className={"h-5"} />
                </Select.Icon>
              </Select.Trigger>
              <Select.Content>
                <Select.Viewport>
                  {Object.values(Tabs)
                    .sort((a, b) => (a === tab ? -1 : 1))
                    .map((tab) => (
                      <Select.Item key={tab} value={tab}>
                        <Select.ItemText>{capitalise(tab)}</Select.ItemText>
                      </Select.Item>
                    ))}
                </Select.Viewport>
              </Select.Content>
            </Select.Select>{" "}
            <h1 className="text-xl">Activities</h1>
          </div>
          <div className="flex items-center gap-2">
            <TextInput
              id="SearchInput"
              value={query}
              onValueChange={(e) => setQuery(e.target.value)}
              icon={
                <Search className="absolute bottom-2.5 left-2.5 top-2.5 text-gray-500" size={18} />
              }
              placeholder="Search"
              className="!h-10 md:w-64"
            />

            <Button
              text="New Activity"
              variant="primary"
              onClick={() => setCreateDialogOpen(true)}
            />
          </div>
        </div>

        <div className="h-full min-h-[20rem] flex-1">
          {loading ? (
            <div className={"flex flex-col gap-4 py-4"}>
              <InterventionCardSkeleton />
              <InterventionCardSkeleton />
              <InterventionCardSkeleton />
            </div>
          ) : (
            <>
              {error ? (
                <div
                  className={
                    "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                  }>
                  <AlertCircle size="36" strokeWidth="1.25" />
                  <p className={"mt-2 text-sm text-gray-600"}>We had an unexpected error</p>
                  <p className={"mt-2 text-sm"}>{error.message}</p>
                </div>
              ) : (
                <>
                  {interventionItems !== undefined && interventionItems.length > 0 ? (
                    <div className={"flex h-full gap-4 "}>
                      <div className={"flex h-full w-full flex-1 flex-col gap-2 "}>
                        {interventionItems
                          .filter((i) => {
                            if (tab === Tabs.Published) {
                              return !i.draft && !i.archived;
                            }

                            if (tab === Tabs.Draft) {
                              return i.draft && !i.archived;
                            }

                            if (tab === Tabs.Archived) {
                              return i.archived;
                            }

                            return true;
                          })
                          .map((intervention) => (
                            <InterventionCard
                              anySelected={selectedIntervention !== null}
                              sticky={isInterventionCardSelected(intervention)}
                              key={intervention.id}
                              intervention={intervention}
                              isSelected={isInterventionCardSelected(intervention)}
                              onClick={() => onInterventionCardClick(intervention)}
                            />
                          ))}
                      </div>
                      <div
                        className={clsx(
                          "sticky top-2 flex max-h-screen flex-1 animate-intro-fade flex-col items-center overflow-auto rounded-lg border-[1px] border-gray-200 px-4 pt-4",
                          selectedIntervention === null ? "bg-transparent" : "bg-white/70",
                        )}>
                        {selectedIntervention !== null ? (
                          <>
                            <div className="mb-4 flex w-full items-center justify-between gap-1 border-b-2 border-gray-200 px-1 pb-2">
                              <h2 className="w-full  text-xl ">{selectedIntervention.name}</h2>
                              <IconButton
                                onClick={() => setDuplicateInterventionId(selectedIntervention.id)}>
                                <Copy />
                              </IconButton>
                              <IconButton onClick={() => editIntervention(selectedIntervention.id)}>
                                <Edit3 />
                              </IconButton>
                            </div>
                            <InterventionPreview intervention={selectedIntervention} />
                          </>
                        ) : (
                          <div className="mt-8 animate-intro-fade text-center text-gray-600">
                            <h3 className="font-bold">No activity selected</h3>
                            <p className="text-sm">Select an activity to get started</p>
                          </div>
                        )}
                      </div>
                    </div>
                  ) : (
                    <div
                      className={
                        "mt-12 flex h-60 flex-col items-center justify-center py-4 text-gray-500"
                      }>
                      <AlertCircle size="36" strokeWidth="1.25" />
                      <h4 className="mt-2 font-bold text-gray-900">No activities found</h4>
                      <p className={"mt-2 text-sm text-gray-600"}>
                        {`We couldn't find any activities`}
                      </p>
                    </div>
                  )}
                </>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};

InterventionsPage.getLayout = function getLayout(page: ReactElement) {
  return <Page>{page}</Page>;
};

export default InterventionsPage;
